import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Bell, Code2, Loader2, MessageSquare, RotateCcw, Send } from 'lucide-react'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import {
  getOriginalCaseNotifications,
  type OriginalCaseNotification,
  type OriginalCaseNotifications,
} from '@/services/originalCaseCabinetService'
import {
  getPedagogyReviewQueueErrorNotifications,
  type PedagogyReviewQueueErrorNotification,
} from '@/services/pedagogyReviewQueueService'
import {
  getWorkBoardNotifications,
  markAllWorkBoardNotificationsRead,
  markWorkItemNotificationsRead,
  type WorkBoardNotification,
  type WorkBoardNotifications,
} from '@/services/workBoardService'

type QueueErrorNotifications = { unreadCount: number; items: PedagogyReviewQueueErrorNotification[] }

// Общий колокольчик УК: персональные события доски плюс служебные очереди супер-админов.
type BellItem =
  | { kind: 'case'; at: string; item: OriginalCaseNotification }
  | { kind: 'queue_error'; at: string; item: PedagogyReviewQueueErrorNotification }
  | { kind: 'work'; at: string; item: WorkBoardNotification }

function formatDate(value: string | null | undefined) {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${match[3]}.${match[2]}` : value ?? ''
}

function formatTime(value: string | null | undefined) {
  if (!value) return ''
  const match = value.match(/T(\d{2}):(\d{2})/)
  return match ? `${match[1]}:${match[2]}` : value
}

export function OriginalCaseNotificationsBell() {
  const navigate = useNavigate()
  const { isSuperAdmin } = useSuperAdmin()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<OriginalCaseNotifications>({ unread_count: 0, items: [] })
  const [queueErrors, setQueueErrors] = useState<QueueErrorNotifications>({ unreadCount: 0, items: [] })
  const [work, setWork] = useState<WorkBoardNotifications>({ unread_count: 0, items: [] })
  const rootRef = useRef<HTMLDivElement | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // Источники независимы: сбой одного не должен гасить другой. Служебные
      // очереди грузим только супер-админу, доску — любому сотруднику УК.
      const [board, cases, errors] = await Promise.allSettled([
        getWorkBoardNotifications(),
        isSuperAdmin === true ? getOriginalCaseNotifications() : Promise.resolve({ unread_count: 0, items: [] }),
        isSuperAdmin === true ? getPedagogyReviewQueueErrorNotifications() : Promise.resolve({ unreadCount: 0, items: [] }),
      ])
      setWork(board.status === 'fulfilled' ? board.value : { unread_count: 0, items: [] })
      setData(cases.status === 'fulfilled' ? cases.value : { unread_count: 0, items: [] })
      setQueueErrors(errors.status === 'fulfilled' ? errors.value : { unreadCount: 0, items: [] })
    } finally {
      setLoading(false)
    }
  }, [isSuperAdmin])

  useEffect(() => {
    void load()
    const interval = window.setInterval(() => {
      void load()
    }, 60000)
    return () => window.clearInterval(interval)
  }, [isSuperAdmin, load])

  useEffect(() => {
    const refresh = () => { void load() }
    window.addEventListener('pedagogy-queue-error-acknowledged', refresh)
    window.addEventListener('work-board-notifications-changed', refresh)
    return () => {
      window.removeEventListener('pedagogy-queue-error-acknowledged', refresh)
      window.removeEventListener('work-board-notifications-changed', refresh)
    }
  }, [load])

  useEffect(() => {
    if (!open) return undefined
    const handleClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const openCase = async (item: OriginalCaseNotification) => {
    setOpen(false)
    navigate(`/management/original/cases?case=${item.id}`)
  }

  const openQueueError = async (item: PedagogyReviewQueueErrorNotification) => {
    setOpen(false)
    navigate(`${pedagogyPaths.reviewDistribution}?mode=skips&skipView=developers`)
  }

  const openWorkItem = async (item: WorkBoardNotification) => {
    setOpen(false)
    try {
      await markWorkItemNotificationsRead(item.item_id)
      setWork(current => ({
        unread_count: Math.max(0, current.unread_count - current.items.filter(row => row.item_id === item.item_id).length),
        items: current.items.filter(row => row.item_id !== item.item_id),
      }))
    } finally {
      window.dispatchEvent(new Event('work-board-notifications-changed'))
      navigate(`/management/work?item=${item.item_id}`)
    }
  }

  const markAllWorkRead = async () => {
    try {
      await markAllWorkBoardNotificationsRead()
      setWork({ unread_count: 0, items: [] })
    } finally {
      window.dispatchEvent(new Event('work-board-notifications-changed'))
    }
  }

  const unreadCount = data.unread_count + queueErrors.unreadCount + work.unread_count
  const bellItems: BellItem[] = [
    ...work.items.map(item => ({ kind: 'work' as const, at: item.created_at, item })),
    ...data.items.map(item => ({ kind: 'case' as const, at: item.created_at, item })),
    ...queueErrors.items.map(item => ({ kind: 'queue_error' as const, at: item.decidedAt, item })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen(current => !current)
          if (!open) void load()
        }}
        className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Уведомления"
        aria-label="Уведомления"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-amber-400 px-1 text-[10px] font-semibold leading-4 text-slate-950">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-96 overflow-hidden rounded-md border border-slate-200 bg-card shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
            <div>
              <div className="text-sm font-semibold text-slate-900">Уведомления</div>
              <div className="text-xs text-slate-500">Непрочитано: {unreadCount.toLocaleString('ru-RU')}</div>
            </div>
            <div className="flex items-center gap-2">
              {work.unread_count > 0 && (
                <button
                  type="button"
                  onClick={() => void markAllWorkRead()}
                  className="text-xs font-medium text-amber-700 hover:text-amber-800"
                  title="Прочитать все уведомления доски"
                >
                  Прочитать все
                </button>
              )}
              {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {bellItems.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500">Нет уведомлений, требующих внимания</div>
            ) : (
              bellItems.map(entry => entry.kind === 'work' ? (
                <button
                  key={`work-${entry.item.notification_id}`}
                  type="button"
                  onClick={() => void openWorkItem(entry.item)}
                  className="block w-full border-b border-slate-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-amber-50/60"
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
                      {entry.item.kind === 'comment' ? <MessageSquare size={14} />
                        : entry.item.kind === 'new_item' ? <Send size={14} />
                          : entry.item.kind === 'acceptance_returned' ? <RotateCcw size={14} />
                            : <ArrowRight size={14} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">#{entry.item.item_id} · {entry.item.item_title}</div>
                      <div className="mt-0.5 line-clamp-2 text-xs font-medium text-amber-700">
                        {entry.item.kind === 'new_item' ? 'Новая задача'
                          : entry.item.kind === 'comment' ? `Новый комментарий${entry.item.note ? `: ${entry.item.note}` : ''}`
                            : entry.item.kind === 'request_rework' ? `Запрос вернули на доработку${entry.item.note ? `: ${entry.item.note}` : ''}`
                              : `Результат вернули в работу${entry.item.note ? `: ${entry.item.note}` : ''}`}
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-slate-500">
                        <span className="truncate">{entry.item.actor_name || 'Сотрудник'}</span>
                        <span className="shrink-0">{formatTime(entry.item.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ) : entry.kind === 'queue_error' ? (
                <button
                  key={`queue-error-${entry.item.decisionId}`}
                  type="button"
                  onClick={() => void openQueueError(entry.item)}
                  className="block w-full border-b border-slate-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-amber-50/60"
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
                      <Code2 size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">
                        Студия №{entry.item.studioNumber}{entry.item.cityName ? ` · ${entry.item.cityName}` : ''}
                      </div>
                      <div className="mt-0.5 line-clamp-2 text-xs font-medium text-amber-700">
                        {entry.item.queueAction === 'exclude_lesson_request_studio'
                          ? 'Просят убрать студию из выдачи отсмотров'
                          : 'Просят убрать занятие из выдачи отсмотров'}
                        {entry.item.reportedDetails ? ` — ${entry.item.reportedDetails}` : ''}
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-slate-500">
                        <span className="truncate">Занятие {formatDate(entry.item.trainingDate)} · {entry.item.decidedByName || 'Руководитель'}</span>
                        <span className="shrink-0">{formatTime(entry.item.decidedAt)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ) : (
                <button
                  key={`case-${entry.item.id}`}
                  type="button"
                  onClick={() => void openCase(entry.item)}
                  className="block w-full border-b border-slate-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-amber-50/60"
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
                      <ArrowRight size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">
                        {entry.item.object_display_name || `Кейс #${entry.item.id}`}
                      </div>
                      <div className="mt-0.5 line-clamp-2 text-xs font-medium text-amber-700">{entry.item.event_label}</div>
                      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-slate-500">
                        <span className="truncate">Передал: {entry.item.created_by_name || '—'}</span>
                        <span className="shrink-0">Создан: {formatTime(entry.item.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="flex border-t border-slate-200">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/management/work')
              }}
              className="flex-1 px-3 py-2 text-left text-sm font-medium text-amber-700 hover:bg-amber-50"
            >
              Запросы в разработку
            </button>
            {isSuperAdmin === true && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  navigate('/management/original/cases')
                }}
                className="border-l border-slate-200 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50"
              >
                Кабинет кейсов
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
